import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Alert, Button, TextInput, Label, Spinner, Badge } from 'flowbite-react';
import { api } from '../api';
import { useToast } from '../components/ToastProvider';

export default function BotSettings() {
  const { appUuid } = useParams();
  const { addToast } = useToast();
  const [app, setApp] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ bot_token: '', bot_username: '' });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get(`/app-dashboard?app_uuid=${encodeURIComponent(appUuid)}`);
      setApp(data.app);
      setForm({ bot_token: data.app?.bot_token || '', bot_username: data.app?.bot_username || '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [appUuid]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/apps/${appUuid}`, { bot_token: form.bot_token, bot_username: form.bot_username });
      addToast({ type: 'success', message: 'Настройки бота сохранены' });
      await load();
    } catch (e) {
      setError(e.message);
      addToast({ type: 'error', message: 'Не удалось сохранить' });
    } finally {
      setSaving(false);
    }
  };

  const setWebhook = async () => {
    setBusy(true);
    try {
      await api.post(`/apps/${appUuid}/set-webhook`, {});
      addToast({ type: 'success', message: 'Webhook установлен' });
      await load();
    } catch (e) {
      setError(e.message);
      addToast({ type: 'error', message: 'Ошибка установки webhook' });
    } finally {
      setBusy(false);
    }
  };

  const deleteWebhook = async () => {
    setBusy(true);
    try {
      await api.post(`/apps/${appUuid}/delete-webhook`, {});
      addToast({ type: 'success', message: 'Webhook удален' });
      await load();
    } catch (e) {
      setError(e.message);
      addToast({ type: 'error', message: 'Ошибка удаления webhook' });
    } finally {
      setBusy(false);
    }
  };

  const hasWebhook = Boolean(app?.webhook_uuid);
  const hasToken = Boolean(form.bot_token);

  return (
    <div className="grid gap-4 max-w-2xl">
      <Card>
        <h2 className="text-lg font-semibold">Чат-бот</h2>
        {error && <Alert color="failure" className="mt-2">{error}</Alert>}
        {loading && <div className="text-sm text-gray-500">Загрузка...</div>}
        {app && (
          <div className="grid gap-4 mt-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Статус:</span>
              {hasToken ? (
                hasWebhook ? (
                  <Badge color="success">Webhook активен</Badge>
                ) : (
                  <Badge color="warning">Webhook не настроен</Badge>
                )
              ) : (
                <Badge color="failure">Bot token не задан</Badge>
              )}
            </div>

            <div>
              <Label value="Bot token" />
              <TextInput value={form.bot_token} onChange={e => setForm({ ...form, bot_token: e.target.value })} placeholder="12345:ABC..." />
            </div>
            <div>
              <Label value="Bot username" />
              <TextInput value={form.bot_username} onChange={e => setForm({ ...form, bot_username: e.target.value })} placeholder="mybot" />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button color="blue" onClick={save} disabled={saving}>{saving ? (<span className="inline-flex items-center gap-2"><Spinner size="sm" /> Сохранение...</span>) : 'Сохранить'}</Button>
              <Button color="success" onClick={setWebhook} disabled={!hasToken || busy}>{busy ? (<span className="inline-flex items-center gap-2"><Spinner size="sm" /> Выполняется...</span>) : 'Установить webhook'}</Button>
              <Button color="failure" onClick={deleteWebhook} disabled={!hasToken || busy}>Удалить webhook</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}